/*
 * <cart-recommendations> — lazily fetches the cart-recs section (sections/cart-recs.liquid)
 * and injects its inner HTML into the drawer.
 *
 * CartRecTips — shared tooltip hydration for cart upsells (used by BOTH the
 * <cart-recommendations> list above and the mini-cart drawer's ygh-upsell rows).
 * The tooltip TEXT is not stored anywhere cart-side — it is lifted from the
 * anchor product's own PDP, so the accessory tooltips merchants maintain in the
 * product template (the aN_info settings on the hero) are the single source of
 * truth. The anchor's URL comes in on [data-rec-tip-src]; we fetch that page
 * once, map data-acc-product-id -> .ua-tip__text, cache the map in
 * sessionStorage, and reveal the (i) only for products that have a tooltip on
 * the PDP. No fetch happens when the list renders no (i) shells.
 */
class CartRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(text => {

        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('cart-recommendations');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          CartRecTips.hydrateAll(this);
        }
      })
      .catch(e => {
        console.error(e);
      });
  }
}
customElements.define('cart-recommendations', CartRecommendations);

var CartRecTips = {
  CACHE_PREFIX: 'ygh:rectips:',
  _pending: {},

  hydrateAll: function (scope) {
    var self = this;
    (scope || document).querySelectorAll('[data-rec-tip-src]').forEach(function (host) {
      self.hydrate(host);
    });
  },

  hydrate: function (host) {
    var url = host.getAttribute('data-rec-tip-src');
    var tips = host.querySelectorAll('[data-rec-tip]');
    if (!url || !tips.length) return;

    this.getMap(url).then(function (map) {
      tips.forEach(function (btn) {
        var text = map[btn.getAttribute('data-rec-pid')];
        var slot = btn.querySelector('.cart-rec-tip__text');
        if (text && slot) {
          slot.textContent = text;
          btn.hidden = false;
        }
      });
    }).catch(function () { /* no tooltips is a fine outcome */ });
  },

  getMap: function (url) {
    var key = this.CACHE_PREFIX + url;
    try {
      var cached = sessionStorage.getItem(key);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (e) { /* storage unavailable */ }

    if (this._pending[url]) return this._pending[url];

    this._pending[url] = fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var map = {};
        doc.querySelectorAll('input[data-acc-product-id]').forEach(function (input) {
          var card = input.closest('.ua-card');
          var tip = card && card.querySelector('.ua-tip__text');
          if (tip && tip.textContent.trim()) {
            map[input.getAttribute('data-acc-product-id')] = tip.textContent.trim();
          }
        });
        try { sessionStorage.setItem(key, JSON.stringify(map)); } catch (e) { /* best effort */ }
        return map;
      });
    return this._pending[url];
  }
};

/*
 * Open/close + placement for the (i) tooltips. Delegated at document level
 * once, so it survives every drawer re-render (the whole drawer's innerHTML is
 * replaced and re-hydrated after cart changes). Hover/focus display is pure
 * CSS; the click path is for touch. The bubble defaults to sitting ABOVE its
 * row (the upsell list ends near the drawer's bottom on mobile) and flips
 * below when the row is too close to the top of its scroll container for the
 * bubble to fit — measured against the drawer's scroll area, not the viewport,
 * because that is where an absolutely-positioned bubble actually clips.
 */
(function () {
  if (window.__yghRecTipToggle) return;
  window.__yghRecTipToggle = true;

  function placeBubble(btn) {
    var bubble = btn.querySelector('.cart-rec-tip');
    if (!bubble) return;
    var container = btn.closest('.ygh-cart__scroll') || btn.closest('[data-rec-tip-src]');
    var topRef = container ? container.getBoundingClientRect().top : 0;
    bubble.classList.toggle('cart-rec-tip--below', btn.getBoundingClientRect().top - topRef < 150);
  }

  function closeAll(except) {
    document.querySelectorAll('.cart-rec-info.is-open').forEach(function (b) {
      if (b !== except) { b.classList.remove('is-open'); b.setAttribute('aria-expanded', 'false'); }
    });
  }

  document.addEventListener('mouseover', function (e) {
    var btn = e.target.closest('[data-rec-tip]');
    if (btn) placeBubble(btn);
  });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-rec-tip]');
    if (!btn) { closeAll(null); return; }
    e.preventDefault();
    e.stopPropagation();
    closeAll(btn);
    var open = btn.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) placeBubble(btn);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll(null);
  });

  /* initial page: hydrate anything already server-rendered */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { CartRecTips.hydrateAll(document); });
  } else {
    CartRecTips.hydrateAll(document);
  }
})();
